import { inject, Service, signal } from '@angular/core';
import { PRIMARY_OUTLET, Router } from '@angular/router';

import { WolfAiSessionStateService } from '@advania-cosoftware/wolf-ai';
import { SessionService } from '@advania-cosoftware/wolf-core';

type Workspace = 'customer' | 'internal';

@Service()
export class WorkspaceNavigationService {
    private readonly _router = inject(Router);
    private readonly _session = inject(SessionService);
    private readonly _aiSession = inject(WolfAiSessionStateService);
    private readonly _internalUrl = signal<string | null>(null);
    private readonly _customerUrl = signal<string | null>(null);
    private _userId: string | null = null;

    remember(url: string): void {
        if (!this._syncUser()) {
            return;
        }

        const tree = this._router.parseUrl(url);
        const segments = tree.root.children[PRIMARY_OUTLET]?.segments;
        const firstSegment = segments?.[0]?.path;
        if (firstSegment === 'internal') {
            if (segments?.[1]?.path === 'ai-chat') {
                const sessionId = this._aiSession.sessionId();
                if (sessionId) {
                    tree.queryParams = { ...tree.queryParams, sessionId };
                    url = this._router.serializeUrl(tree);
                }
            }
            this._internalUrl.set(url);
        } else if (firstSegment && firstSegment !== 'login') {
            this._customerUrl.set(url);
        }
    }

    lastUrl(workspace: Workspace): string | null {
        if (!this._syncUser()) {
            return null;
        }

        return workspace === 'internal' ? this._internalUrl() : this._customerUrl();
    }

    private _syncUser(): boolean {
        const user = this._session.user();
        const userId = user?.objectId ?? user?.email ?? null;
        if (userId !== this._userId) {
            this._userId = userId;
            this._internalUrl.set(null);
            this._customerUrl.set(null);
        }

        return userId !== null;
    }
}
